import React, { useEffect, useRef } from 'react';
import { BounceLoader } from 'react-spinners';
import { Warning, CheckCircle, Info, X } from '@phosphor-icons/react';
import colors from '@Common/assets/colors';
import { useProfileContext } from '@Context/ProfileContext';

interface APISettingsProps {}

const APISettings: React.FC<APISettingsProps> = () => {
  const {
    setTestRailCreds,
    testRailCreds,
    isProcessingTestRail,
    handleTestrailVerification,
    testrailKeyStatus,
    handleUpdateTestrail,
    isSavingTestrail,

    //NEW JIRA PROPS
    jiraCreds,
    isProcessingJira,
    jiraKeyStatus,
    handleUpdateJira,
    handleJiraVerification,
    isSavingJira,
    setJiraCreds,
  } = useProfileContext();

  const [showInstructionModal, setShowInstructionModal] =
    React.useState<boolean>(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showInstructionModal) {
        setShowInstructionModal(false);
      }
    };

    if (showInstructionModal) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showInstructionModal]);

  return (
    <div className="flex h-full w-full flex-col gap-3 p-2">
      <div className="flex flex-col">
        <div className="flex w-full flex-col">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-blue-900">
                Manage API Keys
              </h2>
              <button
                onClick={() => setShowInstructionModal(true)}
                className="rounded-full p-1 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                title="View setup instructions"
                data-testid="api-keys-info-button"
              >
                <Info size={20} weight="bold" />
              </button>
            </div>
            <p className="text-sm text-blue-900">
              Here you can change your API Keys and testrail username. These
              settings are associated to your account.
            </p>
          </div>
          {/* Grouped Card: AHA! API Key + Testrail Credentials */}
          <div className="mt-4 flex flex-col gap-4 rounded-lg border border-gray-400 bg-white p-4">
            {/* Testrail Credentials */}
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              <label className="max-w-[22rem] flex-[0.2] px-2 font-bold lg:ps-10">
                Testrail Credentials
              </label>
              <div className="flex flex-grow items-center">
                <div className="w-full">
                  <input
                    data-testid="testrail_username"
                    disabled={false}
                    defaultValue={testRailCreds.testrail_username}
                    onChange={(e) =>
                      setTestRailCreds({
                        ...testRailCreds,
                        testrail_username: e.target.value,
                      })
                    }
                    placeholder="Testrail Username"
                    className="w-full rounded-lg border border-gray-300 p-1 px-2 font-normal text-gray-600 outline-none"
                  />
                  <div className="spacer h-[10px]"></div>
                  <input
                    data-testid="testrail_api_key"
                    disabled={false}
                    type="password"
                    onChange={(e) =>
                      setTestRailCreds({
                        ...testRailCreds,
                        testrail_api_key: e.target.value,
                      })
                    }
                    defaultValue={testRailCreds.testrail_api_key}
                    placeholder="Testrail API Key"
                    className="w-full rounded-lg border border-gray-300 p-1 px-2 font-normal text-gray-600 outline-none"
                  />
                </div>
                {isProcessingTestRail ? (
                  <div className="flex items-center justify-center px-2">
                    <BounceLoader size={20} color={colors.brandWarning[500]} />
                  </div>
                ) : (
                  <button
                    data-testid="testrail_verify_btn"
                    className="ml-2 rounded-lg bg-primary-700 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500"
                    onClick={handleTestrailVerification}
                  >
                    Verify
                  </button>
                )}
              </div>
            </div>

            {testrailKeyStatus.status === 'ERROR' && (
              <div className="flex items-center justify-center font-bold text-red-500">
                <Warning size={20} weight="bold" className="mr-2" />
                <span>{testrailKeyStatus.message}</span>
              </div>
            )}
            {testrailKeyStatus.status === 'VERIFIED' && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center font-bold text-green-500">
                  <CheckCircle size={20} weight="bold" className="mr-2" />
                  <span>{testrailKeyStatus.message}</span>
                </div>
                <button
                  className="flex items-center rounded-lg bg-primary-700 p-1 px-3 font-normal tracking-wide text-white hover:bg-primary-400"
                  onClick={handleUpdateTestrail}
                >
                  {isSavingTestrail ? (
                    <>
                      <BounceLoader
                        size={20}
                        color={colors.brandWarning[500]}
                      />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            )}
            {testrailKeyStatus.status === 'SUCCESS' && (
              <div className="flex items-center justify-center text-green-500">
                <CheckCircle size={20} weight="bold" className="mr-2" />
                <span>{testrailKeyStatus.message}</span>
              </div>
            )}

            <div className="my-4 h-px bg-gray-300/60"></div>

            {/*JIRA SECTION*/}
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              <label className="max-w-[22rem] flex-[0.2] px-2 font-bold lg:ps-10">
                Jira Credentials
              </label>
              <div className="flex flex-grow items-center">
                <div className="w-full">
                  <input
                    data-testid="jira_email"
                    disabled={false}
                    value={jiraCreds?.email || ''}
                    type="email"
                    onChange={(e) =>
                      setJiraCreds({
                        ...jiraCreds,
                        email: e.target.value,
                      })
                    }
                    //defaultValue={jiraCreds.email}
                    placeholder="Jira Email"
                    className="w-full rounded-lg border border-gray-300 p-1 px-2 font-normal text-gray-600 outline-none"
                  />
                  <div className="spacer h-[10px]"></div>
                  <input
                    data-testid="jira_token"
                    disabled={false}
                    value={jiraCreds?.token || ''}
                    type="password"
                    onChange={(e) =>
                      setJiraCreds({
                        ...jiraCreds,
                        token: e.target.value,
                      })
                    }
                    //defaultValue={jiraCreds.token}
                    placeholder="Jira Token"
                    className="w-full rounded-lg border border-gray-300 p-1 px-2 font-normal text-gray-600 outline-none"
                  />

                  <div className="spacer h-[10px]"></div>
                </div>

                {isProcessingJira ? (
                  <div className="flex items-center justify-center px-2">
                    <BounceLoader size={20} color={colors.brandWarning[500]} />
                  </div>
                ) : (
                  <button
                    data-testid="jira_verify_btn"
                    className="ml-2 rounded-lg bg-primary-700 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500"
                    onClick={handleJiraVerification}
                  >
                    Verify
                  </button>
                )}
              </div>
            </div>

            {jiraKeyStatus.status === 'ERROR' && (
              <div className="flex items-center justify-center font-bold text-red-500">
                <Warning size={20} weight="bold" className="mr-2" />
                <span>{jiraKeyStatus.message}</span>
              </div>
            )}
            {jiraKeyStatus.status === 'VERIFIED' && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center font-bold text-green-500">
                  <CheckCircle size={20} weight="bold" className="mr-2" />
                  <span>{jiraKeyStatus.message}</span>
                </div>
                <button
                  className="flex items-center rounded-lg bg-primary-700 p-1 px-3 font-normal tracking-wide text-white hover:bg-primary-400"
                  onClick={handleUpdateJira}
                >
                  {isSavingJira ? (
                    <>
                      <BounceLoader
                        size={20}
                        color={colors.brandWarning[500]}
                      />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            )}
            {jiraKeyStatus.status === 'SUCCESS' && (
              <div className="flex items-center justify-center text-green-500">
                <CheckCircle size={20} weight="bold" className="mr-2" />
                <span>{jiraKeyStatus.message}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* API Keys Instruction Modal */}
      {showInstructionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowInstructionModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3
                id="modal-title"
                className="text-xl font-semibold text-gray-900"
              >
                API Keys Setup Instructions
              </h3>
              <button
                ref={closeButtonRef}
                onClick={() => setShowInstructionModal(false)}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                data-testid="close-instruction-modal"
                aria-label="Close instructions modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* JIRA INSTRUCTIONS */}
              <div className="rounded-lg border border-blue-300 bg-blue-50 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-lg font-semibold text-blue-900">
                  <Info size={20} weight="bold" />
                  Jira API Token Setup
                </h4>
                <ol className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      1
                    </span>
                    <span>
                      Go to{' '}
                      <a
                        href="https://id.atlassian.com/manage-profile/security/api-tokens"
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        Atlassian API Tokens
                      </a>
                      .
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      2
                    </span>
                    <span>
                      Log in and click <strong>Create API token</strong>.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      3
                    </span>
                    <span>
                      Label it (e.g., "AI Test Case Generator") and click
                      Create.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      4
                    </span>
                    <span>
                      Copy the token and paste it into the "Jira Token" field
                      above.
                    </span>
                  </li>
                </ol>
              </div>

              {/* TestRail Instructions */}
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-lg font-semibold text-green-900">
                  <Info size={20} weight="bold" />
                  TestRail API Key Setup
                </h4>
                <ol className="space-y-2 text-sm text-green-800">
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      1
                    </span>
                    <span>Go to My Settings under the username dropdown</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      2
                    </span>
                    <span>Click the API Keys tab</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      3
                    </span>
                    <span>Click Add Key</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      4
                    </span>
                    <span>Input a name or title for the key</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      5
                    </span>
                    <span>Select Generate Key</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      6
                    </span>
                    <span>Click the Add Key button</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                      7
                    </span>
                    <span>Click the Save Configuration button</span>
                  </li>
                </ol>
              </div>

              {/* Close button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowInstructionModal(false)}
                  className="hover:bg-primary-800 rounded-lg bg-primary-700 px-4 py-2 text-white"
                  data-testid="close-instruction-modal-footer"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APISettings;
